import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContentContainer,
  Search,
  Flex,
  Text,
  Checkbox,
  Divider,
  Counter,
} from '@vibe/core';
import { Filter } from '@vibe/icons';
import { useFilterStore, type CompletionFilter } from '../../stores/filterStore';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FilterDropdownProps {
  /** Board options for workspace mode — when provided and non-empty, show Boards tab */
  boardOptions?: Array<{ id: number; name: string }>;
  /** When true, show Status tab with completion filter options */
  hasStatusColumn?: boolean;
}

/** Filter sections shown as tabs in the dropdown */
type FilterTab = 'boards' | 'status';

// ─── Completion options ──────────────────────────────────────────────────────

const COMPLETION_OPTIONS: { value: CompletionFilter; label: string }[] = [
  { value: 'all', label: 'All items' },
  { value: 'incomplete', label: 'Incomplete only' },
  { value: 'completed', label: 'Completed only' },
];

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Popover/dropdown that exposes filter controls for Boards (workspace mode)
 * and Status (when status column mapped).
 *
 * A badge on the trigger button shows the total count of active filters.
 * Inside, a tab strip lets users switch between filter dimensions.
 */
const FilterDropdown: React.FC<FilterDropdownProps> = ({
  boardOptions,
  hasStatusColumn,
}) => {
  const {
    selectedBoardIds,
    completionFilter,
    setSelectedBoardIds,
    setCompletionFilter,
  } = useFilterStore();

  const showBoardsTab = Boolean(boardOptions && boardOptions.length > 0);
  const showStatusTab = Boolean(hasStatusColumn);

  const defaultTab: FilterTab = showBoardsTab ? 'boards' : 'status';
  const [activeTab, setActiveTab] = useState<FilterTab>(defaultTab);
  const [searchValue, setSearchValue] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);

  // Fall back if the current tab becomes hidden. When neither tab is available
  // the component renders nothing, so skip — otherwise the two branches would
  // ping-pong activeTab and loop forever.
  useEffect(() => {
    if (!showBoardsTab && !showStatusTab) return;
    if (activeTab === 'boards' && !showBoardsTab) setActiveTab('status');
    if (activeTab === 'status' && !showStatusTab) setActiveTab('boards');
  }, [activeTab, showBoardsTab, showStatusTab]);

  // Total active filters
  const completionFilterActive = completionFilter !== 'all' ? 1 : 0;
  const totalActiveFilters = selectedBoardIds.length + completionFilterActive;

  // ── Board toggle ──────────────────────────────────────────────────────────

  const toggleBoard = useCallback(
    (id: number) => {
      const next = selectedBoardIds.includes(id)
        ? selectedBoardIds.filter((b) => b !== id)
        : [...selectedBoardIds, id];
      setSelectedBoardIds(next);
    },
    [selectedBoardIds, setSelectedBoardIds],
  );

  // ── Search filtering ──────────────────────────────────────────────────────

  const lowerSearch = searchValue.toLowerCase();

  const filteredBoards = useMemo(
    () =>
      (boardOptions ?? []).filter((b) =>
        b.name.toLowerCase().includes(lowerSearch),
      ),
    [boardOptions, lowerSearch],
  );

  // Reset search when switching tabs
  const handleTabChange = useCallback((tab: FilterTab) => {
    setActiveTab(tab);
    setSearchValue('');
  }, []);

  // Don't render if neither tab is available
  if (!showBoardsTab && !showStatusTab) return null;

  return (
    <div className="rp-filter-dropdown" ref={triggerRef}>
      <Dialog
        zIndex={1000}
        content={
          <DialogContentContainer
            className="rp-filter-dropdown__content"
            size={DialogContentContainer.sizes.MEDIUM}
          >
            {/* Tab strip */}
            <div className="rp-filter-dropdown__tabs">
              {showBoardsTab && (
                <button
                  type="button"
                  className={`rp-tab ${activeTab === 'boards' ? 'rp-tab--active' : ''}`}
                  onClick={() => handleTabChange('boards')}
                >
                  Boards
                  {selectedBoardIds.length > 0 && (
                    <Counter
                      count={selectedBoardIds.length}
                      size={Counter.sizes.SMALL}
                      color={Counter.colors.PRIMARY}
                      className="rp-filter-dropdown__tab-count"
                    />
                  )}
                </button>
              )}
              {showStatusTab && (
                <button
                  type="button"
                  className={`rp-tab ${activeTab === 'status' ? 'rp-tab--active' : ''}`}
                  onClick={() => handleTabChange('status')}
                >
                  Status
                  {completionFilter !== 'all' && (
                    <Counter
                      count={1}
                      size={Counter.sizes.SMALL}
                      color={Counter.colors.PRIMARY}
                      className="rp-filter-dropdown__tab-count"
                    />
                  )}
                </button>
              )}
            </div>

            <Divider />

            {/* Search input — hidden for Status tab (only 3 fixed options) */}
            {activeTab !== 'status' && (
              <div className="rp-filter-dropdown__search">
                <Search
                  size="small"
                  value={searchValue}
                  onChange={(value: string) => setSearchValue(value)}
                  placeholder={`Search ${activeTab}...`}
                />
              </div>
            )}

            {/* List area */}
            <div className="rp-filter-dropdown__list">
              {/* ── Boards ────────────────────────────────────────────── */}
              {activeTab === 'boards' && (
                <>
                  {filteredBoards.length === 0 && (
                    <Text
                      type={Text.types.TEXT2}
                      className="rp-filter-dropdown__empty"
                    >
                      No boards found
                    </Text>
                  )}
                  {filteredBoards.map((board) => (
                    <label key={board.id} className="rp-filter-dropdown__option">
                      <Checkbox
                        checked={selectedBoardIds.includes(board.id)}
                        onChange={() => toggleBoard(board.id)}
                      />
                      <Text type={Text.types.TEXT2}>{board.name}</Text>
                    </label>
                  ))}
                </>
              )}

              {/* ── Status (completion filter) ────────────────────────── */}
              {activeTab === 'status' && (
                <div className="rp-filter-dropdown__status-list">
                  {COMPLETION_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`rp-filter-dropdown__status-option ${
                        completionFilter === option.value
                          ? 'rp-filter-dropdown__status-option--active'
                          : ''
                      }`}
                      onClick={() => setCompletionFilter(option.value)}
                    >
                      <Text type={Text.types.TEXT2}>{option.label}</Text>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </DialogContentContainer>
        }
        hideTrigger={['clickoutside', 'esckey']}
        showTrigger={['click']}
        position="bottom-start"
      >
        <Button
          size={Button.sizes.SMALL}
          kind={Button.kinds.TERTIARY}
          leftIcon={Filter}
        >
          Filter
        </Button>
      </Dialog>
    </div>
  );
};

export default FilterDropdown;
